import type { PitchSkinId } from "@golazo/shared";

/**
 * Los nombres del mundo de la carrera.
 *
 * Internacionales, con un poso español y otro brasileño, y con el humor puesto
 * donde duele: en el futbol como negocio. En el barrio se juega por el bar que
 * paga las camisetas; en Primera, por el fondo de inversion que paga todo lo
 * demas. Como los clubes suben y bajan, con los años acaba habiendo un "Bar
 * Manolo" en Primera — y esa es precisamente la gracia.
 *
 * Ninguno es un club real ni se le parece a proposito, salvo los dos guiños
 * que pidio el usuario (ver `FIXED_CLUBS`).
 *
 * Los nombres propios no se traducen (como los de los clubes de verdad). Lo que
 * se traduce son los rasgos (`QUIRKS`), que son frases.
 */
export const CLUB_NAMES: readonly (readonly string[])[] = [
  // Liga del Barrio
  [
    "Bar Manolo FC",
    "Deportivo Descampado",
    "Sunday Hangover FC",
    "Real Tupperware",
    "Atlético Rotonda",
    "Inter de la Esquina",
    "Esporte Clube Fiado",
    "Los Cuñados United",
    "Dynamo Chanclas",
    "Sporting Parking",
    "Olympique Kebab",
    "AC Barbacoa",
  ],
  // Regional
  [
    "UD Resaca",
    "Grêmio Boleto",
    "Hipermercado United",
    "Racing Autovía",
    "Polideportivo Municipal",
    "FC Overdraft",
    "Borussia Burnout",
    "Athletic Rebajas",
    "Recreativo Tragaperras",
    "Real Siesta",
    "Wanderers Gasolinera",
    "Unión Barbacoa",
  ],
  // Segunda
  [
    "Real Hipoteca",
    "Sporting Promesas",
    "Inter Comisión",
    "Olympique Burbuja",
    "Deportivo Presupuesto",
    "CF Palco VIP",
    "Náutico Sin Mar",
    "Atlético Recalificación",
    "Club Subvención",
    "Racing Patrocinador",
    "Lokomotiv Ladrillo",
    "Wolves de la Deuda",
  ],
  // Primera
  [
    "Real Galáctico",
    "Fondo de Inversión FC",
    "Sporting Superliga",
    "Real Petrodólar",
    "Deportivo Canal de Pago",
    "Olympique Offshore",
    "Athletic Cláusula",
    "FC Derechos de Imagen",
    "Holding United",
    "Merchandising City",
    "AC Crypto",
    "Inter Influencer",
  ],
];

/**
 * Clubes que existen en TODOS los mundos, sea cual sea la semilla.
 *
 * - Ytuano FC: guiño al Ituano de Itu, el pueblo donde crecio el usuario. En
 *   la Liga del Barrio, rojinegro a listas como el Galo. No es tu club por defecto: el
 *   primer club lo elige el jugador al crear la carrera.
 * - Malaka FC: guiño a Malaga, en Segunda. De celeste (no hay blanquiazul a
 *   rayas en el catalogo, y es lo mas cercano).
 */
export interface FixedClub {
  tier: number;
  name: string;
  kitId: string;
  quirk: string;
  /** Posicion en el reparto de fuerza de su division (0 = el mas flojo). */
  strengthRank: number;
  stadium: StadiumSeed;
}

/**
 * Un estadio: su nombre y el cesped sobre el que se juega. El nombre y el
 * cesped van juntos a proposito, porque el chiste es la pareja: "El
 * Frigorifico" es de hielo, la "Crypto.bet Arena" es de neon y en el metaverso
 * no hay publico.
 */
export interface StadiumSeed {
  name: string;
  skin: PitchSkinId;
}

export const FIXED_CLUBS: readonly FixedClub[] = [
  {
    tier: 0,
    name: "Ytuano FC",
    // Rojo y negro a listas, como el Galo.
    kitId: "rojinegro",
    quirk: "En su pueblo todo es gigante: el semáforo, la cabina, la ilusión. El presupuesto, no.",
    strengthRank: 2,
    // Itu es "la ciudad de los exageros".
    stadium: { name: "Estádio dos Exageros", skin: "sunburst" },
  },
  {
    tier: 2,
    name: "Malaka FC",
    kitId: "celeste",
    quirk: "Presupuesto de Champions, ideas de chiringuito.",
    strengthRank: 5,
    stadium: { name: "Espeto Arena", skin: "sunburst" },
  },
];

/** Estadios por division. Cada club del mundo tiene uno distinto. */
export const STADIUMS: readonly (readonly StadiumSeed[])[] = [
  // Liga del Barrio: tierra, cristales y cuatro padres
  [
    { name: "El Descampado", skin: "cropCircles" },
    { name: "Pista del Colegio", skin: "terrazzo" },
    { name: "Campo del Tío Paco", skin: "floodlit" },
    { name: "Solar de la Gasolinera", skin: "sunburst" },
    { name: "El Patatal", skin: "cropCircles" },
    { name: "La Jaula", skin: "terrazzo" },
    { name: "Parque de los Perros", skin: "floodlit" },
    { name: "El Cementerio de Tobillos", skin: "herringbone" },
    { name: "La Rotonda Grande", skin: "sunburst" },
    { name: "Polideportivo Sin Vestuarios", skin: "terrazzo" },
  ],
  // Regional: una grada, a veces
  [
    { name: "Municipal Sin Grada", skin: "floodlit" },
    { name: "La Nave Industrial", skin: "terrazzo" },
    { name: "Campo Anexo al Tanatorio", skin: "aurora" },
    { name: "El Barrizal", skin: "cropCircles" },
    { name: "Polígono Arena", skin: "herringbone" },
    { name: "Nuestra Señora del Córner", skin: "sunburst" },
    { name: "La Olla Exprés", skin: "magmaVein" },
    { name: "El Frigorífico", skin: "crystalShard" },
    { name: "Municipal Pendiente de Obras", skin: "splitDream" },
    { name: "Campo El Pino Solitario", skin: "floodlit" },
  ],
  // Segunda: estadios con nombre de promesa electoral
  [
    { name: "Estadio La Recalificación", skin: "checkerMow" },
    { name: "Arena Promesas Electorales", skin: "splitDream" },
    { name: "Nuevo Estadio (Viejo)", skin: "herringbone" },
    { name: "Estadio del Ladrillo", skin: "sunburst" },
    { name: "La Caldera Fría", skin: "magmaVein" },
    { name: "Estadio Presupuesto Prorrogado", skin: "floodlit" },
    { name: "Coliseo Subvencionado", skin: "checkerMow" },
    { name: "El Mirador de la Deuda", skin: "crystalShard" },
    { name: "Campo Náutico de Secano", skin: "tide" },
    { name: "Estadio Palco con Vistas", skin: "herringbone" },
  ],
  // Primera: el nombre es de quien paga
  [
    { name: "Naming Rights Arena", skin: "checkerMow" },
    { name: "Crypto.bet Arena", skin: "neonHex" },
    { name: "Coliseo Galáctico", skin: "checkerMow" },
    { name: "Estadio Fondo Soberano", skin: "sunburst" },
    { name: "Mega Arena Hospitality", skin: "synthScan" },
    { name: "Estadio de las Cláusulas", skin: "herringbone" },
    { name: "Metaverso Park", skin: "aurora" },
    { name: "Teatro de los Sueños Financiados", skin: "floodlit" },
    { name: "Monumental Offshore", skin: "magmaVein" },
    { name: "Estadio del Derecho de Imagen", skin: "crystalShard" },
  ],
];

export const COACH_SURNAMES: readonly string[] = [
  "Garrido",
  "Van der Bosch",
  "Kowalczyk",
  "Rossetti",
  "Da Silva",
  "Petrović",
  "O'Leary",
  "Hoffmann",
  "Dubois",
  "Sanchís",
  "Okafor",
  "Lindqvist",
  "Yamada",
  "Moreira",
  "Castelli",
  "Barrios",
];

const STAR_FIRST: readonly string[] = [
  "Kike",
  "Jonny",
  "Rafa",
  "Moha",
  "Yeray",
  "Luka",
  "Kofi",
  "Sven",
  "Dimitri",
  "Enzo",
  "Thiago",
  "Kenji",
  "Paddy",
  "Mats",
  "Joaquim",
  "Ilya",
];

const STAR_LAST: readonly string[] = [
  "Tornado",
  "Paredes",
  "Van Hout",
  "Kovač",
  "Oyelaran",
  "Bergström",
  "Rinaldi",
  "Ferreira",
  "Novak",
  "Quiroga",
  "Almansa",
  "Delacroix",
  "Okonkwo",
  "McAllister",
  "Takahashi",
  "Maldonado",
];

export function starName(first: number, last: number): string {
  return `${STAR_FIRST[first % STAR_FIRST.length]} ${STAR_LAST[last % STAR_LAST.length]}`;
}
export const STAR_NAME_SPACE = STAR_FIRST.length * STAR_LAST.length;

/**
 * Lo que se dice de cada club. Uno por club, fijo: es su fama.
 * Son claves de `t()`.
 */
export const QUIRKS: readonly string[] = [
  "Su presidente está imputado, pero ilusionado.",
  "Entrenan en un parking. Lo cuentan con orgullo.",
  "Su estrella cobra en bocadillos.",
  "Llevan tres años sin ganar fuera. Ni a las cartas.",
  "Tienen más ultras que socios.",
  "Su portero es el hijo del presidente. Se nota.",
  "El césped es de plástico. Las ideas, también.",
  "Ponen la música de Rocky en el vestuario. No funciona.",
  "Su delantero centro tiene 41 años y dos hernias.",
  "Han fichado a un influencer. Tiene más seguidores que minutos.",
  "Su himno dura nueve minutos. Nadie se lo sabe.",
  "Celebran los córners como si fueran goles.",
  "Su dueño vive en otro huso horario y opina en todos.",
  "Tienen un director deportivo, un director de contenidos y ningún lateral.",
  "Pagan las fichas en criptomonedas. Este mes valen un bocadillo.",
  "Su estadio lleva el nombre de una casa de apuestas. Y se nota en las alineaciones.",
];

/** Tu agente. Se llama asi y cobra asi. */
export const AGENT_NAME = "Paco Diezporciento";
/** El periodico. Sale todos los dias y acierta alguno. */
export const PAPER_NAME = "El Pregonero";
